import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useCreatePaymentRegistryMutation } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (registryId: string) => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalBody = styled.form`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  user-select: none;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ErrorAlert = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #ef444422;
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 12px;
`;

export function CreateRegistryModal({ open, onClose, onCreated }: Props) {
  const [createRegistry, { isLoading }] = useCreatePaymentRegistryMutation();
  const todayStr = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState(`Реестр платежей от ${new Date().toLocaleDateString('ru-RU')}`);
  const [date, setDate] = useState(todayStr);
  const [autoInclude, setAutoInclude] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        titleInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Укажите название реестра платежей');
      return;
    }
    try {
      setError(null);
      const res = await createRegistry({
        title: title.trim(),
        registry_date: date || undefined,
        auto_include_unassigned: autoInclude,
        notes: notes.trim() || undefined,
        source_type: 'manual',
      }).unwrap();
      onClose();
      if (onCreated) onCreated(res.id);
    } catch (err: unknown) {
      setError(`Ошибка создания реестра: ${String(err)}`);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        ref={modalContentRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-registry-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="create-registry-modal-title">Создание нового Реестра платежей</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <ModalBody onSubmit={handleSubmit}>
          {error && <ErrorAlert>{error}</ErrorAlert>}

          <FormGroup>
            <Label>Название реестра*</Label>
            <Input
              ref={titleInputRef}
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Например: Реестр платежей на 21.10.2026"
            />
          </FormGroup>

          <FormGroup>
            <Label>Дата реестра</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={autoInclude}
                onChange={(e) => setAutoInclude(e.target.checked)}
              />
              Автоматически включить нераспределённые заявки и остатки (-ОСТ)
            </CheckboxLabel>
          </FormGroup>

          <FormGroup>
            <Label>Примечания</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Комментарий для казначея / финконтроля…"
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button $variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Создание…' : 'Создать реестр'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
